import { authService } from '@/services/auth-service';
import axios from 'axios';
import { toast } from 'sonner';
import { reportApiFailure, prepareApiRequest } from '@/observability/sentry';

interface QueueItem {
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
  request: any;
}

let isRefreshing = false;
let refreshQueue: QueueItem[] = [];

const baseURL = '';

const getSchoolURL = () => {
  try {
    const school_Url = localStorage.getItem('schoolBackendURL');
    if (school_Url) {
      return school_Url + '/api/';
    } else {
      authService.logout();
      return '';
    }
  } catch {
    authService.logout();
    return '';
  }
};

const axiosInstance = axios.create({
  baseURL: baseURL,
  timeout: 20000,
  //@ts-expect-error - TODO: fix this
  skipIntercept: false,
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  async (config) => {
    const accessToken = localStorage.getItem('accessToken');
    const schoolURL = getSchoolURL();

    const schoolIdentifier = localStorage.getItem('schoolIdentifier');

    config.baseURL = schoolURL;
    prepareApiRequest(config);

    if (accessToken) {
      //@ts-expect-error - TODO: fix this
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${accessToken}`,
        'X-School-URL': schoolIdentifier,
      };
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => {
    reportApiFailure(response.config, response.status, Number(response.data?.status), undefined, response.headers['x-request-id']);
    // Check if data.status exists and indicates an issue (not 200)
    if (
      response.data.status &&
      response.data.status !== 200 &&
      !(response.config as any).suppressStatusToast
    ) {
      // Show the toast for user feedback
      toast.error(response.data.message);
      // But DO NOT throw an error. Let the promise resolve successfully.
      // Downstream code (like useQuery) can check response.data.status if needed.
    }
    // Always return the response for successful HTTP status codes
    return response;
  },
  async function (error) {
    reportApiFailure(error.config || {}, error.response?.status, Number(error.response?.data?.status), error.code, error.response?.headers?.['x-request-id']);
    if (error.response?.status !== 401) {
      toast.error(error?.response?.data?.message || 'Oops something went wrong!');
    }

    const originalRequest = error.config;

    if (typeof error.response === 'undefined') {
      console.log('A server/network error occurred. Looks like CORS might be the problem.');
      return Promise.reject(error);
    }

    if (
      error.response.status === 401 &&
      //   error.response.statusText === 'Unauthorized' &&
      originalRequest.url === '/token/refresh/'
    ) {
      console.log('Refresh token expired or invalid');
      authService.logout();
      return Promise.reject(error);
    }

    if (
      error.response.status === 401 &&
      //   error.response.statusText === 'Unauthorized' &&
      //@ts-expect-error - TODO: fix this
      !axiosInstance.defaults.skipIntercept
    ) {
      console.log('Access token has expired, refreshing...');
      const refreshToken = localStorage.getItem('refreshToken');

      if (refreshToken) {
        if (!isRefreshing) {
          isRefreshing = true;

          try {
            const response = await axiosInstance.post('/token/refresh/', {
              refresh: refreshToken,
            });

            localStorage.setItem('accessToken', response.data.access);
            axiosInstance.defaults.headers.common['Authorization'] =
              'Bearer ' + response.data.access;

            // Process queued requests
            refreshQueue.forEach(({ resolve, reject, request }) => {
              if (request.headers) {
                request.headers = {
                  ...request.headers,
                  Authorization: 'Bearer ' + response.data.access,
                };
              }
              axiosInstance(request).then(resolve).catch(reject);
            });

            refreshQueue = [];
            isRefreshing = false;
            return axiosInstance(originalRequest);
          } catch (refreshError) {
            console.warn('Token refresh failed');
            authService.logout();
            return Promise.reject(refreshError);
          }
        } else {
          // Queue the request if refresh is already in progress
          return new Promise((resolve, reject) => {
            refreshQueue.push({
              resolve,
              reject,
              request: originalRequest,
            });
          });
        }
      } else {
        console.log('No refresh token available');
        authService.logout();
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
